// Editing a person's own details.
//
// The contact page used to read "read-only here · edit on the person" with no
// such path anywhere in the CRM — a door that was never installed, which is what
// sent Jacky looking for it. This is that path.
//
// Every write goes through `update_contact` as a proposal (ADR-003): the face
// never writes a table.
import { useState } from "react";
import { Button } from "@aiw/ui";
import { useProposal } from "../hooks/slice-a";
import { selectCls } from "./job-bits";

/** What the command accepts. The command carries the same list and validates
 *  against a zod enum, so if the two ever drift the edit is REFUSED with a
 *  message rather than writing a type nobody asked for — a loud failure, which
 *  is why a small local copy is safe here. */
const TYPES = [
  { value: "consumer", label: "Direct consumer" },
  { value: "business", label: "Business" },
  { value: "supplier", label: "Supplier" },
  { value: "staff", label: "Staff" },
] as const;

export type EditableContact = {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  contact_type: string | null;
  updated_at: string;
  /** The customer's own address — one free line, and the one a Proposal offers as the work site. */
  address?: string | null;
};

/** The live table holds "Direct" and "Consumer" for the same idea — both predate
 *  this command. Match on the stem so an existing row selects the right option
 *  instead of silently defaulting to the first one. */
function currentType(contact_type: string | null): (typeof TYPES)[number]["value"] {
  const t = (contact_type ?? "").toLowerCase();
  if (t.startsWith("business")) return "business";
  if (t.startsWith("supplier")) return "supplier";
  if (t.startsWith("staff")) return "staff";
  return "consumer";
}

export function ContactEdit({ contact }: { contact: EditableContact }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(contact.name ?? "");
  const [phone, setPhone] = useState(contact.phone ?? "");
  const [email, setEmail] = useState(contact.email ?? "");
  const [type, setType] = useState(currentType(contact.contact_type));
  const [address, setAddress] = useState(contact.address ?? "");
  const proposal = useProposal();

  // Only the fields that actually changed are sent. `null` clears a phone or an
  // email; leaving a field out means "leave it alone" — a distinction the
  // command relies on, so it cannot be expressed by sending empty strings.
  const args: Record<string, unknown> = { contact_id: contact.id };
  if (name.trim() !== (contact.name ?? "")) args.name = name.trim();
  if (phone.trim() !== (contact.phone ?? "")) args.phone = phone.trim() || null;
  if (email.trim() !== (contact.email ?? "")) args.email = email.trim() || null;
  if (type !== currentType(contact.contact_type)) args.contact_type = type;
  if (address.trim() !== (contact.address ?? "")) args.address = address.trim() || null;
  const changed = Object.keys(args).length - 1;
  args.expected_updated_at = contact.updated_at;

  // A blank-looking phone passes a naive "not empty" check and would replace a
  // working number with punctuation. Same rule the command enforces, stated here
  // so the button explains itself instead of failing after a round trip.
  const phoneHasNoDigits = phone.trim() !== "" && !/\d/.test(phone);

  if (!open) {
    return (
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        Edit details
      </Button>
    );
  }

  return (
    <form
      className="grid gap-3 text-sm md:grid-cols-2"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!changed || phoneHasNoDigits) return;
        const r = await proposal.run("update_contact", args, "Contact details corrected");
        // The command answers "no change" rather than writing an audit row that
        // claims an edit nobody made, so say that rather than a bare success.
        const result = r?.status === "executed" ? (r.result as { changed?: string[] } | null) : null;
        if (r?.status === "executed" && result && Array.isArray(result.changed) && result.changed.length === 0) return;
        if (r?.status === "executed") setOpen(false);
      }}
    >
      <label>
        Name
        <input className={selectCls + " mt-1 w-full"} value={name} onChange={(e) => setName(e.target.value)} required maxLength={200} />
      </label>
      <label>
        Type
        <select className={selectCls + " mt-1 w-full"} value={type} onChange={(e) => setType(e.target.value as typeof type)}>
          {TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </label>
      <label>
        Phone
        <input
          className={selectCls + " mt-1 w-full"}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          inputMode="tel"
          maxLength={40}
          placeholder="Leave blank to clear"
          aria-invalid={phoneHasNoDigits}
        />
      </label>
      <label>
        Email
        <input
          className={selectCls + " mt-1 w-full"}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          inputMode="email"
          maxLength={200}
          placeholder="Leave blank to clear"
        />
      </label>
      {/* THE CUSTOMER'S ADDRESS. Jacky: "under contact, customer address cannot be edited. it should be
          edited, and selectable when setup proposal." It is their own address, not the site of a job —
          a job keeps its own — so the same person can have one here and several sites on their jobs,
          and the Proposal builder offers both. */}
      <label className="md:col-span-2">
        Customer address
        <input
          className={selectCls + " mt-1 w-full"}
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          maxLength={500}
          placeholder="Leave blank to clear"
        />
      </label>
      {phoneHasNoDigits && (
        <p role="alert" className="md:col-span-2 text-sm text-destructive">
          That phone has no digits in it — it would replace a working number with punctuation.
        </p>
      )}
      <p className="md:col-span-2 text-micro text-muted-foreground">
        {changed === 0
          ? "Nothing has been changed yet."
          : `Changing ${changed} field${changed === 1 ? "" : "s"}. Clearing a phone or email removes it as a way of finding this person.`}
      </p>
      <div className="flex gap-2 md:col-span-2">
        <Button size="sm" type="submit" disabled={proposal.busy || changed === 0 || phoneHasNoDigits}>
          Save details
        </Button>
        <Button
          size="sm"
          variant="outline"
          type="button"
          onClick={() => {
            setName(contact.name ?? "");
            setPhone(contact.phone ?? "");
            setEmail(contact.email ?? "");
            setType(currentType(contact.contact_type));
            setAddress(contact.address ?? "");
            setOpen(false);
          }}
        >
          Cancel
        </Button>
      </div>
      {proposal.note && (
        <p role={proposal.note.tone === "bad" ? "alert" : "status"} className="md:col-span-2 text-sm">
          {proposal.note.text}
        </p>
      )}
    </form>
  );
}
